import { Icon, Modal } from 'Components';
import { useTranslation } from 'react-i18next';
import styles from './UInassignTrainingModal.module.scss';
import Button from '../Button';

interface UnassignTrainingModalProps {
	visible: boolean;
	onSubmit: () => void;
	onCancel: () => void;
	loading?: boolean;
	// bulk assignment mode
	assignmentCount?: number;
	learnerCount?: number;
	// course mode
	courseCount?: number;
}

const UnassignTrainingModal: React.FC<UnassignTrainingModalProps> = ({
	visible,
	onSubmit,
	onCancel,
	loading = false,
	assignmentCount,
	learnerCount,
	courseCount
}) => {
	const { t } = useTranslation();

	const isBulk = assignmentCount !== undefined;
	const isCourse = courseCount !== undefined;
	const hasNoLearners = (isBulk || isCourse) && learnerCount === 0;

	return (
		<Modal
			visible={visible}
			onCancel={loading ? undefined : onCancel}
			closable={!loading}
			maskClosable={!loading}
			className={styles.unassignModalRoot}
			footer={
				<div className={styles.unassignModal__actions}>
					<Button
						variant="borderless"
						size="small"
						onClick={onCancel}
						disabled={loading}
					>
						{t('learning.courseAssignments.unassignModal.cancel')}
					</Button>
					<Button
						size="small"
						onClick={onSubmit}
						loading={loading}
						disabled={hasNoLearners}
					>
						{t('learning.courseAssignments.unassignModal.unassign')}
					</Button>
				</div>
			}
			centered
			size="xs"
		>
			<div className={styles.unassignModal}>
				<div className={styles.unassignModal__title}>
					{isCourse
						? t('learning.courseAssignments.unassignModal.unassignCourses')
						: t('learning.courseAssignments.unassignModal.unassignTraining')}
				</div>

				{isCourse ? (
					<div className={styles.unassignModal__wrapper}>
						<div className={styles.unassignModal__description}>
							{t(
								'learning.courseAssignments.unassignModal.courseDescription'
							)}
						</div>
						<ul className={styles.unassignModal__list}>
							<li>
								{t('learning.courseAssignments.unassignModal.affect')}{' '}
								{learnerCount === undefined ? (
									<Icon
										type="loading"
										aria-label={t(
											'learning.courseAssignments.unassignModal.countingUsers'
										)}
									/>
								) : (
									t('learning.courseAssignments.unassignModal.learners', {
										count: learnerCount
									})
								)}{' '}
								{t('learning.courseAssignments.unassignModal.total')}
							</li>
						</ul>
					</div>
				) : isBulk ? (
					<div className={styles.unassignModal__wrapper}>
						<div className={styles.unassignModal__description}>
							{t(
								'learning.courseAssignments.unassignModal.trainingDescription'
							)}
						</div>

						<ul className={styles.unassignModal__list}>
							<li>
								{t('learning.courseAssignments.unassignModal.affect')}{' '}
								{t(
									'learning.courseAssignments.unassignModal.assignments',
									{ count: assignmentCount }
								)}
							</li>

							<li>
								{t('learning.courseAssignments.unassignModal.affect')}{' '}
								{learnerCount === undefined ? (
									<Icon
										type="loading"
										aria-label={t(
											'learning.courseAssignments.unassignModal.countingUsers'
										)}
									/>
								) : (
									t('learning.courseAssignments.unassignModal.learners', {
										count: learnerCount
									})
								)}{' '}
								{t('learning.courseAssignments.unassignModal.total')}
							</li>
						</ul>
						{hasNoLearners && (
							<div className={styles.unassignModal__description}>
								{t(
									'learning.courseAssignments.unassignModal.noLearners'
								)}
							</div>
						)}
					</div>
				) : (
					<div className={styles.unassignModal__wrapper}>
						<div className={styles.unassignModal__description}>
							{t(
								'learning.courseAssignments.unassignModal.noLearnersStarted'
							)}
						</div>
						<div className={styles.unassignModal__description}>
							{t(
								'learning.courseAssignments.unassignModal.removeFromLearners'
							)}
						</div>
					</div>
				)}

				<div className={styles.unassignModal__warning}>
					<Icon type="warningFilledIcon" role="img" />
					{isCourse
						? t('learning.courseAssignments.unassignModal.courseWarning')
						: t('learning.courseAssignments.unassignModal.trainingWarning')}
				</div>
			</div>
		</Modal>
	);
};

export default UnassignTrainingModal;